import React, { useState } from 'react';
import { Bell, BellOff, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { useSelector } from 'react-redux';
import { jwtDecode } from 'jwt-decode';

const BudgetAccountTable = ({ budgetAccountData = [], handleToggleAlert }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Get tokens and user info
  const idToken = useSelector((state) => state.idToken.value);
  const decoded = jwtDecode(idToken);
  const userName = `${decoded.given_name} ${decoded.family_name}`;
  const userEmail = decoded.email || '';

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (percentage) => {
    return `${percentage.toFixed(1)}%`;
  };

  // Get status color based on percentage
  const getStatusColor = (percentage) => {
    if (percentage >= 100) return "text-red-600";
    if (percentage >= 80) return "text-yellow-600";
    return "text-green-600";
  };

  // Local wrapper for toggle alert that includes user info
  const onToggleAlert = (account) => {
    handleToggleAlert(account, userName, userEmail);
  };

  // Check if current user is receiving alerts for an account
  const isUserReceivingAlerts = (account) => {
    return Array.isArray(account.Users) && 
      (account.Users.includes(userName) || account.Users.includes(userEmail));
  };

  // Paginate the data
  const paginatedData = budgetAccountData.slice(
    (currentPage - 1) * itemsPerPage, 
    currentPage * itemsPerPage
  );

  return (
    <div className="bg-white rounded-lg shadow-lg border border-gray-200">
      <div className="p-6">
        <h3 className="text-2xl font-bold text-[#003366] mb-6">
          Budget by Account
        </h3>

        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#003366]"></div>
          </div>
        ) : error ? (
          <div className="text-red-500 text-center p-4">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-[#003366]/5">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Account Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Account No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Budget
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Current Cost
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Beyond Budget
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Percentage
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                    Notifications
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.length > 0 ? (
                  paginatedData.map((account, index) => (
                    <tr key={account.id || index} className="hover:bg-[#003366]/5">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {account.AccountName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {account.AccountNo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(account.Budget)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatCurrency(account.currentCost)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {account.beyondBudget > 0 ? (
                          <div className="flex items-center">
                            <AlertTriangle className="w-4 h-4 text-red-500 mr-1" />
                            <span className="text-red-600 font-medium">
                              {formatCurrency(account.beyondBudget)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-green-600">$0</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`font-medium ${getStatusColor(account.percentage)}`}>
                          {formatPercentage(account.percentage)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button 
                          onClick={() => onToggleAlert(account)}
                          className="flex items-center text-sm font-medium"
                        >
                          {isUserReceivingAlerts(account) ? (
                            <>
                              <BellOff className="w-5 h-5 text-gray-500 mr-1" />
                              <span className="text-gray-500">Receiving Alerts</span>
                            </>
                          ) : (
                            <>
                              <Bell className="w-5 h-5 text-[#003366] mr-1" />
                              <span className="text-[#003366]">Alert Me</span>
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                      No budget data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {budgetAccountData.length > 0 && (
              <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
                <div className="flex items-center text-sm text-gray-500">
                  <span>Showing {Math.min(budgetAccountData.length, (currentPage - 1) * itemsPerPage + 1)} to {Math.min(budgetAccountData.length, currentPage * itemsPerPage)} of {budgetAccountData.length} results</span>
                  <div className="ml-4">
                    <select 
                      className="border border-gray-300 rounded p-1 text-sm"
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={5}>5 per page</option>
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                  </div>
                </div>
                
                <div className="flex items-center gap-1">
                  {/* First page button */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-label="First page"
                  >
                    <span className="flex">
                      <ChevronLeft className="h-4 w-4" />
                      <ChevronLeft className="h-4 w-4 -ml-2" />
                    </span>
                  </button>
                  
                  {/* Previous page button */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded ${
                      currentPage === 1
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="flex items-center mx-2">
                    {Array.from({ length: Math.min(5, Math.ceil(budgetAccountData.length / itemsPerPage)) }, (_, i) => {
                      const totalPages = Math.ceil(budgetAccountData.length / itemsPerPage);
                      let pageNum;
                      
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 flex items-center justify-center rounded ${
                            currentPage === pageNum
                              ? 'bg-[#003366] text-white'
                              : 'text-gray-700 hover:bg-gray-200'
                          }`}
                          aria-label={`Page ${pageNum}`}
                          aria-current={currentPage === pageNum ? "page" : undefined}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === Math.ceil(budgetAccountData.length / itemsPerPage)}
                    className={`p-2 rounded ${
                      currentPage === Math.ceil(budgetAccountData.length / itemsPerPage)
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(Math.ceil(budgetAccountData.length / itemsPerPage))}
                    disabled={currentPage === Math.ceil(budgetAccountData.length / itemsPerPage)}
                    className={`p-2 rounded ${
                      currentPage === Math.ceil(budgetAccountData.length / itemsPerPage)
                        ? 'text-gray-400 cursor-not-allowed'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                    aria-label="Last page"
                  >
                    <span className="flex">
                      <ChevronRight className="h-4 w-4" />
                      <ChevronRight className="h-4 w-4 -ml-2" />
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BudgetAccountTable;